export enum RoleID {
  ADMIN = 1,
  HEAD_OF_EDUCATION = 2,
  ACADEMIC_OFFICER = 3,
  FINANCE_OFFICER = 4,
  DOCUMENT_OFFICER = 5,
  HUMAN_RESOURCE = 6,
  TEACHER = 7,
  HEAD_OF_DEPARTMENT = 8,
  VICE_DEAN_ADMINISTRATION = 9,
  VICE_DEAN_ACADEMIC = 10,
}

export enum RoleCode {
  ADMIN = "admin",
  HEAD_OF_EDUCATION = "head_of_education",
  ACADEMIC_OFFICER = "academic_officer",
  FINANCE_OFFICER = "finance_officer",
  DOCUMENT_OFFICER = "document_officer",
  HUMAN_RESOURCE = "human_resource",
  TEACHER = "teacher",
  HEAD_OF_DEPARTMENT = "head_of_department",
  VICE_DEAN_ADMINISTRATION = "vice_dean_administration",
  VICE_DEAN_ACADEMIC = "vice_dean_academic",
}

export interface RoleOption {
  id: RoleID;
  code: RoleCode;
  name: RoleCode;
  name_th: string;
  name_en: string;
  label: string;
}

export const ROLE_OPTIONS: RoleOption[] = [
  {
    id: RoleID.ADMIN,
    code: RoleCode.ADMIN,
    name: RoleCode.ADMIN,
    name_th: "แอดมิน",
    name_en: "Admin",
    label: "แอดมิน",
  },
  {
    id: RoleID.HEAD_OF_EDUCATION,
    code: RoleCode.HEAD_OF_EDUCATION,
    name: RoleCode.HEAD_OF_EDUCATION,
    name_th: "หัวหน้างานการศึกษา",
    name_en: "Head of Education",
    label: "หัวหน้างานการศึกษา",
  },
  {
    id: RoleID.ACADEMIC_OFFICER,
    code: RoleCode.ACADEMIC_OFFICER,
    name: RoleCode.ACADEMIC_OFFICER,
    name_th: "เจ้าหน้าที่วิชาการ",
    name_en: "Academic Officer",
    label: "เจ้าหน้าที่วิชาการ",
  },
  {
    id: RoleID.FINANCE_OFFICER,
    code: RoleCode.FINANCE_OFFICER,
    name: RoleCode.FINANCE_OFFICER,
    name_th: "เจ้าหน้าที่การเงิน",
    name_en: "Finance Officer",
    label: "เจ้าหน้าที่การเงิน",
  },
  {
    id: RoleID.DOCUMENT_OFFICER,
    code: RoleCode.DOCUMENT_OFFICER,
    name: RoleCode.DOCUMENT_OFFICER,
    name_th: "เจ้าหน้าที่สารบรรณ",
    name_en: "Document Officer",
    label: "เจ้าหน้าที่สารบรรณ",
  },
  {
    id: RoleID.HUMAN_RESOURCE,
    code: RoleCode.HUMAN_RESOURCE,
    name: RoleCode.HUMAN_RESOURCE,
    name_th: "นักทรัพยากรบุคคล",
    name_en: "Human Resource Officer",
    label: "นักทรัพยากรบุคคล",
  },
  {
    id: RoleID.TEACHER,
    code: RoleCode.TEACHER,
    name: RoleCode.TEACHER,
    name_th: "อาจารย์",
    name_en: "Teacher",
    label: "อาจารย์",
  },
  {
    id: RoleID.HEAD_OF_DEPARTMENT,
    code: RoleCode.HEAD_OF_DEPARTMENT,
    name: RoleCode.HEAD_OF_DEPARTMENT,
    name_th: "หัวหน้าสาขาวิชา",
    name_en: "Head of Department",
    label: "หัวหน้าสาขาวิชา",
  },
  {
    id: RoleID.VICE_DEAN_ADMINISTRATION,
    code: RoleCode.VICE_DEAN_ADMINISTRATION,
    name: RoleCode.VICE_DEAN_ADMINISTRATION,
    name_th: "รองคณบดีฝ่ายบริหาร",
    name_en: "Vice Dean for Administration",
    label: "รองคณบดีฝ่ายบริหาร",
  },
  {
    id: RoleID.VICE_DEAN_ACADEMIC,
    code: RoleCode.VICE_DEAN_ACADEMIC,
    name: RoleCode.VICE_DEAN_ACADEMIC,
    name_th: "รองคณบดีฝ่ายวิชาการ",
    name_en: "Vice Dean for Academic Affairs",
    label: "รองคณบดีฝ่ายวิชาการ",
  },
];

export const RoleObject: Record<keyof typeof RoleID, RoleOption> = {
  ADMIN: ROLE_OPTIONS[0],
  HEAD_OF_EDUCATION: ROLE_OPTIONS[1],
  ACADEMIC_OFFICER: ROLE_OPTIONS[2],
  FINANCE_OFFICER: ROLE_OPTIONS[3],
  DOCUMENT_OFFICER: ROLE_OPTIONS[4],
  HUMAN_RESOURCE: ROLE_OPTIONS[5],
  TEACHER: ROLE_OPTIONS[6],
  HEAD_OF_DEPARTMENT: ROLE_OPTIONS[7],
  VICE_DEAN_ADMINISTRATION: ROLE_OPTIONS[8],
  VICE_DEAN_ACADEMIC: ROLE_OPTIONS[9],
};

export const ROLE_ID_BY_CODE: Partial<Record<string, RoleID>> = ROLE_OPTIONS.reduce(
  (acc, role) => ({
    ...acc,
    [role.code]: role.id,
    [role.name]: role.id,
  }),
  {} as Partial<Record<string, RoleID>>,
);

function normalizeRoleLookupValue(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value).trim();
  }

  return '';
}

export function resolveRoleId(role: { id?: unknown; code?: unknown; name?: unknown } | null | undefined): RoleID | null {
  const code = normalizeRoleLookupValue(role?.code) || normalizeRoleLookupValue(role?.name);
  const roleIdFromCode = ROLE_ID_BY_CODE[code];

  if (roleIdFromCode !== undefined) {
    return roleIdFromCode;
  }

  const roleId = Number(role?.id);
  const roleOption = ROLE_OPTIONS.find((option) => Number(option.id) === roleId);

  return roleOption?.id ?? null;
}

export const SUBJECT_MANAGEMENT_EDIT_ROLE_IDS: RoleID[] = [
  RoleID.ADMIN,
  RoleID.ACADEMIC_OFFICER,
  RoleID.HEAD_OF_EDUCATION,
];

// Master Data : รายวิชา — admins plus the academic workload managers.
export const SUBJECT_MASTER_DATA_ROLE_IDS: RoleID[] = [
  ...SUBJECT_MANAGEMENT_EDIT_ROLE_IDS,
];

export const SUBJECT_MANAGEMENT_VIEW_ONLY_ROLE_IDS: RoleID[] = [];

export const SUBJECT_MANAGEMENT_ROLE_IDS: RoleID[] = [
  ...SUBJECT_MANAGEMENT_EDIT_ROLE_IDS,
  ...SUBJECT_MANAGEMENT_VIEW_ONLY_ROLE_IDS,
];

export const SUBJECT_SCHEDULE_ROLE_IDS: RoleID[] = [
  ...SUBJECT_MANAGEMENT_EDIT_ROLE_IDS,
  RoleID.HEAD_OF_EDUCATION,
  RoleID.TEACHER,
  RoleID.HEAD_OF_DEPARTMENT,
  RoleID.VICE_DEAN_ACADEMIC,
  RoleID.VICE_DEAN_ADMINISTRATION,
];

export const SUBJECT_SCHEDULE_REVIEW_ROLE_IDS: RoleID[] = [
  RoleID.ADMIN,
  RoleID.HEAD_OF_EDUCATION,
  RoleID.TEACHER,
  RoleID.HEAD_OF_DEPARTMENT,
  RoleID.VICE_DEAN_ACADEMIC,
  RoleID.VICE_DEAN_ADMINISTRATION,
];

export const SCHEDULE_CORRECTION_REQUEST_REVIEW_ROLE_IDS: RoleID[] = [
  RoleID.ADMIN,
  RoleID.VICE_DEAN_ACADEMIC,
];

export const TEACH_COMPENSATION_ROLE_IDS: RoleID[] = [
  // RoleID.HUMAN_RESOURCE,
  RoleID.TEACHER,
  RoleID.HEAD_OF_DEPARTMENT,
  RoleID.VICE_DEAN_ACADEMIC,
  RoleID.VICE_DEAN_ADMINISTRATION,
];

export const TEACH_COMPENSATION_MUTATION_ROLE_IDS: RoleID[] =
  TEACH_COMPENSATION_ROLE_IDS.filter((roleId) => roleId !== RoleID.ADMIN);

export const TEACH_COMPENSATION_REVIEW_ROLE_IDS: RoleID[] = [
  RoleID.ADMIN,
  RoleID.VICE_DEAN_ACADEMIC,
];

export const COMPENSATION_SUMMARY_ROLE_IDS: RoleID[] = [
  RoleID.ADMIN,
  RoleID.TEACHER,
  RoleID.HEAD_OF_DEPARTMENT,
  RoleID.VICE_DEAN_ACADEMIC,
  RoleID.VICE_DEAN_ADMINISTRATION,
];

// Navigation visibility is intentionally narrower than page access: admins
// retain direct read access to compensation summaries without seeing the two
// teacher-facing summary entries in the application menu.
export const COMPENSATION_SUMMARY_MENU_ROLE_IDS: RoleID[] =
  COMPENSATION_SUMMARY_ROLE_IDS.filter((roleId) => roleId !== RoleID.ADMIN);

export const ERP_LEAVE_IMPORT_ROLE_IDS: RoleID[] = [
  RoleID.ADMIN,
  RoleID.HUMAN_RESOURCE,
  RoleID.DOCUMENT_OFFICER,
];

export const TEACH_COMPENSATION_REPORT_ROLE_IDS: RoleID[] = [
  RoleID.ADMIN,
  RoleID.HEAD_OF_EDUCATION,
  RoleID.DOCUMENT_OFFICER,
];

export const OVERLOAD_MEMO_ADMIN_ROLE_IDS: RoleID[] = [
  RoleID.ADMIN,
];

export const OVERLOAD_MEMO_REVIEW_ROLE_IDS: RoleID[] = [
  RoleID.ADMIN,
  RoleID.HEAD_OF_EDUCATION,
  RoleID.VICE_DEAN_ACADEMIC,
];

// การจัดการบันทึกข้อความ group — union of admin editor + certifying stations, gates the accordion + parent route — context/decisions/DECISION_RolesPermissions.md
export const OVERLOAD_MEMO_ROLE_IDS: RoleID[] = Array.from(
  new Set([...OVERLOAD_MEMO_ADMIN_ROLE_IDS, ...OVERLOAD_MEMO_REVIEW_ROLE_IDS]),
);

export const TEACH_COMPENSATION_MANAGEMENT_ROLE_IDS: RoleID[] = Array.from(
  new Set([
    ...TEACH_COMPENSATION_ROLE_IDS,
    ...TEACH_COMPENSATION_REVIEW_ROLE_IDS,
    ...TEACH_COMPENSATION_REPORT_ROLE_IDS,
    ...COMPENSATION_SUMMARY_ROLE_IDS,
    ...ERP_LEAVE_IMPORT_ROLE_IDS,
    ...OVERLOAD_MEMO_ROLE_IDS,
  ]),
);

export const UNAVAILABLE_FEATURE_ROLE_IDS: RoleID[] = [
  RoleID.HUMAN_RESOURCE,
];

// Disbursement Document Certification (ขั้นตอนการรับรองเอกสารส่งเบิก) — v1.2
// re-anchored on the customer UAM (r21/r22, การจัดการเอกสารส่งเบิก).
// v2.0 phase 1 is the per-certification chain and only has two stations:
// อาจารย์ -> การเงิน. หัวหน้าสาขา and รองคณบดีฝ่ายบริหาร left the per-cert chain and act
// once per รอบส่งเบิก instead (DISBURSEMENT_ROUND_ROLE_IDS below).
export const DISBURSEMENT_CERTIFY_ROLE_IDS: RoleID[] = [
  RoleID.ADMIN,
  RoleID.TEACHER,
  RoleID.FINANCE_OFFICER,
];

// "เอกสารของฉัน" tab สำหรับผู้บริหารที่สอนจริง แยกเจตนาจาก DISBURSEMENT_CERTIFY_ROLE_IDS — context/decisions/DECISION_RolesPermissions.md
export const DISBURSEMENT_SELF_CERTIFY_ROLE_IDS: RoleID[] = [
  RoleID.HEAD_OF_DEPARTMENT,
  RoleID.VICE_DEAN_ADMINISTRATION,
  RoleID.VICE_DEAN_ACADEMIC,
];

// v2.0 phase 2 รอบส่งเบิก queue (หัวหน้าสาขา→การเงิน→รองบริหาร, การเงิน appears in both per-cert + round queues); ADMIN removed 2026-08-26 (acting_as_admin? bypass deleted, keeps read via DISBURSEMENT_DOCUMENT_VIEW_ROLE_IDS) — context/decisions/DECISION_RolesPermissions.md
export const DISBURSEMENT_ROUND_ROLE_IDS: RoleID[] = [
  RoleID.FINANCE_OFFICER,
  RoleID.HEAD_OF_DEPARTMENT,
  RoleID.VICE_DEAN_ADMINISTRATION,
];

// การจัดทำใบปะหน้า (สร้าง/แก้ไข/ส่งตรวจสอบ/ลบ) — เจ้าหน้าที่สารบรรณ only. ADMIN removed
// 2026-08-26 with the phase-2 bypass above; the backend rejects it at
// DisbursementCoverMemosController#reject_admin_actor! even if this array is bypassed, so the
// two must not drift apart. This is the ACT gate, not the route gate — the page itself opens
// for DISBURSEMENT_COVER_MEMO_VIEW_ROLE_IDS below and hides every button behind this array.
export const DISBURSEMENT_DOCUMENT_OFFICER_ROLE_IDS: RoleID[] = [
  RoleID.DOCUMENT_OFFICER,
];

// ผู้ที่เปิดหน้า "การจัดการบันทึกข้อความเบิกจ่ายค่าตอบแทนการสอน" ได้ = route + menu gate.
// 2026-09-04 customer decision "admin สามารถดู cover-memos ได้แต่สร้างหรือรับรองไม่ได้" —
// 2026-08-26 had narrowed the officer array and, because that array was ALSO the route gate,
// admin lost the page entirely (403 + menu gone), which was more than the defect asked for.
// Third array rather than widening the officer one: ดูได้ ≠ ทำได้, and the previous time these
// two meanings shared an array it cost the registry page its rounds tab (see
// DISBURSEMENT_ROUND_EXPORT_ROLE_IDS below). Backend counterpart:
// DisbursementCoverMemosController::ADMIN_READABLE_ACTIONS.
export const DISBURSEMENT_COVER_MEMO_VIEW_ROLE_IDS: RoleID[] = [
  RoleID.ADMIN,
  ...DISBURSEMENT_DOCUMENT_OFFICER_ROLE_IDS,
];

// READ/EXPORT surface of a รอบส่งเบิก on the registry page — admin keeps it (it is the one
// phase-2 thing 2026-08-26 did NOT take away: DisbursementRoundPolicy#export? still allows
// admin, and #scope still gives it every department). Split out of
// DISBURSEMENT_DOCUMENT_OFFICER_ROLE_IDS that day: registry.component's isRoundExportRole
// piggybacked on that array to mean "admin + สารบรรณ", so narrowing the issuance array to
// สารบรรณ alone silently took the registry's completed-rounds block and status sub-tabs away
// from admin. Two meanings, two arrays — do not re-merge them.
export const DISBURSEMENT_ROUND_EXPORT_ROLE_IDS: RoleID[] = [
  RoleID.ADMIN,
  RoleID.DOCUMENT_OFFICER,
];

// Roles that read the raw station-level ส่งเบิก status. Everyone else sees the
// collapsed "รอดำเนินการ". Deliberately its own const and not a reuse of
// DISBURSEMENT_DOCUMENT_OFFICER_ROLE_IDS — that one gates the ใบปะหน้า ACTIONS, and the two
// policies must be free to diverge.
// Emptied on purpose: admin used to read the granular station name (e.g.
// "รอเข้ารอบส่งเบิก") on the registry/certification pages while เจ้าหน้าที่สารบรรณ read
// it nowhere in practice (the roster view never calls disbursementStatusAudienceFor —
// see registry.component.ts's rosterBadgeFor). Tester feedback asked both to read the
// same collapsed "รอดำเนินการ" อาจารย์ already sees, so no role resolves to 'detailed'
// any more — kept as an array (not deleted) so a future role can opt back in here.
export const DISBURSEMENT_DETAILED_STATUS_ROLE_IDS: RoleID[] = [];

// Vice Dean Academic reviews the teacher's schedule-issue revision branch (A1).
export const DISBURSEMENT_REVISION_REVIEW_ROLE_IDS: RoleID[] = [
  RoleID.ADMIN,
  RoleID.VICE_DEAN_ACADEMIC,
];

// r22 page access = whoever can act on either phase, plus the รองวิชาการ review branch.
export const DISBURSEMENT_CERTIFICATION_PAGE_ROLE_IDS: RoleID[] = Array.from(
  new Set([
    ...DISBURSEMENT_CERTIFY_ROLE_IDS,
    ...DISBURSEMENT_ROUND_ROLE_IDS,
    ...DISBURSEMENT_REVISION_REVIEW_ROLE_IDS,
  ]),
);

// SIT sprint 2 — r22 keeps ONE route but TWO titles: อาจารย์ กับ หัวหน้าสาขา รับรองเอกสาร
// ส่วน role อื่นบนหน้าเดียวกันยังเป็นผู้อนุมัติ. Two disjoint arrays whose union is exactly
// DISBURSEMENT_CERTIFICATION_PAGE_ROLE_IDS — the menu renders one sibling entry per array
// (same url, disjoint roles), so no role gains or loses access to the page.
export const DISBURSEMENT_CERTIFY_TITLE_ROLE_IDS: RoleID[] = [
  RoleID.TEACHER,
  RoleID.HEAD_OF_DEPARTMENT,
];

export const DISBURSEMENT_APPROVE_TITLE_ROLE_IDS: RoleID[] = DISBURSEMENT_CERTIFICATION_PAGE_ROLE_IDS
  .filter((roleId) => !DISBURSEMENT_CERTIFY_TITLE_ROLE_IDS.includes(roleId));

// r21 ข้อมูลเอกสารส่งเบิก route gate (VE x6); row scoping enforced server-side by DisbursementCertificationPolicy.scope — context/decisions/DECISION_RolesPermissions.md
export const DISBURSEMENT_DOCUMENT_VIEW_ROLE_IDS: RoleID[] = [
  RoleID.ADMIN,
  RoleID.FINANCE_OFFICER,
  RoleID.DOCUMENT_OFFICER,
  RoleID.TEACHER,
  RoleID.HEAD_OF_DEPARTMENT,
  RoleID.VICE_DEAN_ACADEMIC,
  RoleID.VICE_DEAN_ADMINISTRATION,
];

export const DISBURSEMENT_MANAGEMENT_ROLE_IDS: RoleID[] = Array.from(
  new Set([
    ...DISBURSEMENT_CERTIFY_ROLE_IDS,
    ...DISBURSEMENT_ROUND_ROLE_IDS,
    ...DISBURSEMENT_DOCUMENT_OFFICER_ROLE_IDS,
    ...DISBURSEMENT_REVISION_REVIEW_ROLE_IDS,
    ...DISBURSEMENT_DOCUMENT_VIEW_ROLE_IDS,
  ]),
);
